import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Avatar,
  Typography,
  Pagination,
  DialogActions,
  Box,
  Divider,
  Grid,
  Chip,
  Alert,
  ImageList,
  ImageListItem,
  Skeleton,
  useTheme,
  useMediaQuery
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import EmailIcon from '@mui/icons-material/Email';
import PhoneIcon from '@mui/icons-material/Phone';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import CakeIcon from '@mui/icons-material/Cake';
import WcIcon from '@mui/icons-material/Wc';
import PhotoLibraryIcon from '@mui/icons-material/PhotoLibrary';
import LockIcon from '@mui/icons-material/Lock';
import { Button } from '@mui/material';
import API_BASE_URL, { BASE_URL } from '../config';
import UpgradeModal from '../components/UpgradeModal';
import useAccess from '../hooks/useAccess';

const UserProfileDialog = ({ selectedUser, openProfileDialog, handleProfileClose }) => {
    const [photos, setPhotos] = useState([]);
    const [currentPage, setCurrentPage] = useState(1);
  const [photosPerPage] = useState(6);
  const [selectedImage, setSelectedImage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [upgradeOpen, setUpgradeOpen] = useState(false);
  const [isPrivate, setIsPrivate] = useState(false);

  const { isPremium, isAdmin, isFree } = useAccess();
  const currentUid = localStorage.getItem('uid');
  const isOwnProfile = selectedUser && currentUid === selectedUser.uid;
  const canViewFull = isAdmin || isPremium || isOwnProfile;

  // Build correct image URL from relative backend path
  const getPhotoUrl = (path) => {
    if (!path) return '/goldpetals/uploads/default-profile.png';
    if (path.startsWith('blob:')) return path;
    if (path.startsWith('http') && !path.includes('localhost')) return path;
    if (path.startsWith('http') && path.includes('localhost')) {
      return path.replace(/http:\/\/localhost:\d+/, BASE_URL);
    }
    if (path.startsWith('/')) return `${BASE_URL}${path}`;
    return `${BASE_URL}/uploads/${path}`;
  };

  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('md'));

    const fetchPhotos = async (userId) => {
        try {
      setLoading(true);
      setError(null);

      // Free users cannot see photos of others
      if (isFree && !isOwnProfile) {
        setPhotos([]);
        return;
      }

            const response = await axios.get(`${API_BASE_URL}/photos`, {
                params: { userId },
                withCredentials: true,
            });
      const data = Array.isArray(response.data) ? response.data : [];
      // FIX: Trust backend response (backend now handles admin/owner visibility logic)
      setPhotos(data);
        } catch (error) {
      if (error.response?.status === 403) {
        setPhotos([]);
      } else if (error.response?.status === 404) {
        setPhotos([]);
      } else {
        setError('No photos uploaded');
      }
    } finally {
      setLoading(false);
        }
    };

    useEffect(() => {
        if (selectedUser) {
      fetchPhotos(selectedUser.id);
        }
    }, [selectedUser, openProfileDialog]);

    const indexOfLastPhoto = currentPage * photosPerPage;
    const indexOfFirstPhoto = indexOfLastPhoto - photosPerPage;
    const currentPhotos = photos.slice(indexOfFirstPhoto, indexOfLastPhoto);
  const totalPages = Math.ceil(photos.length / photosPerPage);

  const InfoItem = ({ icon, label, value }) => (
    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, gap: 1 }}>
      {icon}
      <Box>
        <Typography variant="caption" color="text.secondary">
          {label}
        </Typography>
        <Typography variant="body1">
          {value || 'Not specified'}
        </Typography>
      </Box>
    </Box>
  );

    return (
        selectedUser && (
      <>
        <Dialog 
        open={openProfileDialog} 
        onClose={handleProfileClose} 
        fullWidth 
        maxWidth="md"
        fullScreen={fullScreen}
      >
        <DialogTitle
          sx={{ 
            px: 3, 
            py: 2,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            bgcolor: 'primary.main',
            color: 'white',
            fontSize: '1.25rem',
            fontWeight: 600,
          }}
        >
          <span>Profile Details</span>
          <IconButton
            aria-label="close"
            onClick={handleProfileClose}
            sx={{ color: 'white' }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ p: 3 }}>
          {/* Private profile guard */}
          {isPrivate ? (
            <Box sx={{ textAlign: 'center', py: 6 }}>
              <LockIcon sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
              <Typography variant="h6" color="text.secondary">This profile is private.</Typography>
            </Box>
          ) : (
          <Grid container spacing={3}>
            {/* Profile Header */}
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 3, mt: 1 }}>
                    {selectedUser.image_url ? (
                  // FIX: Wrap in getPhotoUrl to prepend backend origin
                  <Avatar src={getPhotoUrl(selectedUser.image_url)} alt={`${selectedUser.first_name} ${selectedUser.last_name}`} sx={{ width: 120, height: 120 }} />
                    ) : (
                  <Avatar sx={{ width: 120, height: 120, fontSize: '3rem' }}>{selectedUser.first_name[0]}</Avatar>
                )}
                <Box>
                  <Typography variant="h5" component="div" gutterBottom>
                    {selectedUser.first_name} {selectedUser.last_name}
                  </Typography>
                  <Chip label={selectedUser.gender || 'Not specified'} size="small" color="primary" variant="outlined" />
                  {selectedUser._limited && (
                    <Chip
                      label="Limited — Upgrade for full profile"
                      size="small" color="warning" variant="outlined"
                      onClick={() => setUpgradeOpen(true)}
                      sx={{ ml: 1, cursor: 'pointer' }}
                    />
                  )}
                </Box>
              </Box>
            </Grid>

            {/* Contact Information — hidden for free users viewing others */}
            {canViewFull && (
            <Grid item xs={12} md={6}>
              <Typography variant="h6" gutterBottom>Contact Information</Typography>
              <Box sx={{ ml: 1 }}>
                <InfoItem icon={<EmailIcon color="primary" />} label="Email Address" value={selectedUser.email_id} />
                <InfoItem icon={<PhoneIcon color="primary" />} label="Phone Number" value={selectedUser.phone_number} />
                <InfoItem icon={<LocationOnIcon color="primary" />} label="Address"
                  value={`${selectedUser.area || ''}, ${selectedUser.district || ''}, ${selectedUser.state || ''} - ${selectedUser.pincode || ''}`} />
              </Box>
            </Grid>
            )}

            {/* Upgrade prompt for free users */}
            {!canViewFull && (
              <Grid item xs={12}>
                <Alert
                  severity="warning"
                  action={
                    <Button color="inherit" size="small" onClick={() => setUpgradeOpen(true)}>Upgrade</Button>
                  }
                >
                  Upgrade to Premium to view full contact details, address, and photos.
                </Alert>
              </Grid>
            )}

            {/* Personal Information */}
            <Grid item xs={12} md={6}>
              <Typography variant="h6" gutterBottom>Personal Information</Typography>
              <Box sx={{ ml: 1 }}>
                <InfoItem icon={<CakeIcon color="primary" />} label="Age" value={canViewFull ? selectedUser.age : '—'} />
                <InfoItem icon={<WcIcon color="primary" />} label="Gender" value={selectedUser.gender} />
              </Box>
            </Grid>

            {/* Photos Section — premium/admin/own only */}
            {canViewFull && (
            <Grid item xs={12}>
              <Divider sx={{ my: 1 }} />
              <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <PhotoLibraryIcon color="primary" /> Photo Gallery
              </Typography>
              {loading ? (
                <ImageList cols={3} gap={16}>
                  {[1,2,3].map(i => <ImageListItem key={i}><Skeleton variant="rectangular" height={200} /></ImageListItem>)}
                </ImageList>
              ) : error ? (
                <Alert severity="info" sx={{ mt: 2 }}>{error}</Alert>
              ) : photos.length === 0 ? (
                <Alert severity="info" sx={{ mt: 2 }}>No photos uploaded yet.</Alert>
              ) : (
                <>
                  <ImageList cols={3} gap={16}>
                    {currentPhotos.map(photo => (
                      <ImageListItem key={photo.id} sx={{ cursor: 'pointer' }} onClick={() => setSelectedImage(photo)}>
                        <img src={getPhotoUrl(photo.filename)} alt="User Photo" loading="lazy"
                          style={{ height: 200, width: '100%', objectFit: 'cover', borderRadius: 8 }} />
                      </ImageListItem>
                    ))}
                  </ImageList>
                  {totalPages > 1 && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
                      <Pagination count={totalPages} page={currentPage} onChange={(_, v) => setCurrentPage(v)} color="primary" />
                    </Box>
                  )}
                </>
              )}
            </Grid>
            )}
          </Grid>
          )}
                </DialogContent>

        {/* Full-size Image Dialog */}
                <Dialog
                    open={Boolean(selectedImage)}
          onClose={() => setSelectedImage(null)}
          maxWidth="xl"
        >
          <DialogContent sx={{ p: 0 }}>
            {selectedImage && (
              <Box sx={{ position: 'relative' }}>
                <IconButton
                  onClick={() => setSelectedImage(null)}
                  sx={{
                    position: 'absolute',
                    right: 8,
                    top: 8,
                    bgcolor: 'rgba(0, 0, 0, 0.5)',
                    '&:hover': { bgcolor: 'rgba(0, 0, 0, 0.7)' },
                  }}
                >
                  <CloseIcon sx={{ color: 'white' }} />
                        </IconButton>
                            <img
                                src={getPhotoUrl(selectedImage.filename)}
                  alt="Full size"
                  style={{ maxHeight: '90vh', maxWidth: '100%', objectFit: 'contain' }}
                />
              </Box>
                        )}
                    </DialogContent>
                </Dialog>
            </Dialog>

      <UpgradeModal open={upgradeOpen} onClose={() => setUpgradeOpen(false)} featureLabel="Full Profile View" />
      </>
        )
    );
};

export default UserProfileDialog;
